export type FreshnessUnit = "minutes" | "hours" | "days";

export type Freshness =
  | { state: "no_data" }
  | { state: "fresh"; unit: FreshnessUnit; value: number }
  | { state: "stale"; unit: FreshnessUnit; value: number };

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

export function computeFreshness(
  lastActivityAt: string | null,
  now: Date = new Date(),
): Freshness {
  if (lastActivityAt === null) return { state: "no_data" };
  const ageMs = now.getTime() - new Date(lastActivityAt).getTime();
  const state = ageMs >= STALE_THRESHOLD_MS ? "stale" : "fresh";
  if (ageMs < 60_000) return { state, unit: "minutes", value: 1 };
  if (ageMs < 3_600_000) return { state, unit: "minutes", value: Math.floor(ageMs / 60_000) };
  if (ageMs < 86_400_000) return { state, unit: "hours", value: Math.floor(ageMs / 3_600_000) };
  return { state, unit: "days", value: Math.floor(ageMs / 86_400_000) };
}

// --- TTL-aware status freshness (spec: 2026-05-25-waze-crowdsource-comparison §B.3) ---

export type TtlState = "no_data" | "fresh" | "aging" | "stale" | "expired";

export type TtlFreshness = {
  state: TtlState;
  // null when state is "no_data"
  ageMs: number | null;
  ttlMs: number;
  // 0..1, clamped — drives the decay gauge. 1 when no_data, 0 when fresh.
  decay: number;
};

// Cold-start default: 2h. Tunable via VITE_STATUS_TTL_MINUTES once Medellín
// pilot data exists (spec open question #1).
export const DEFAULT_TTL_MINUTES = 120;

// Read once at module load — Vite inlines import.meta.env at build time, so
// the value cannot change at runtime. Avoids parsing on every badge render.
const STATUS_TTL_MS: number = (() => {
  const raw = import.meta.env.VITE_STATUS_TTL_MINUTES;
  const parsed = typeof raw === "string" && raw.length > 0 ? Number(raw) : NaN;
  const minutes = Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_TTL_MINUTES;
  return minutes * 60_000;
})();

export function getStatusTtlMs(): number {
  return STATUS_TTL_MS;
}

export type ComputeOpts = { ttlMs?: number; now?: Date };

export function computeStatusFreshness(
  lastActivityAt: string | null,
  opts: ComputeOpts = {},
): TtlFreshness {
  const ttlMs = opts.ttlMs ?? getStatusTtlMs();
  const now = opts.now ?? new Date();
  if (lastActivityAt === null) {
    return { state: "no_data", ageMs: null, ttlMs, decay: 1 };
  }
  const ageMs = Math.max(0, now.getTime() - new Date(lastActivityAt).getTime());
  const ratio = ageMs / ttlMs;
  const decay = Math.min(1, ratio);
  // Tiers: 0–25% fresh, 25–75% aging, 75–100% stale, >100% expired.
  if (ratio >= 1) return { state: "expired", ageMs, ttlMs, decay: 1 };
  if (ratio >= 0.75) return { state: "stale", ageMs, ttlMs, decay };
  if (ratio >= 0.25) return { state: "aging", ageMs, ttlMs, decay };
  return { state: "fresh", ageMs, ttlMs, decay };
}

// Compact age — "now", "12m", "1h", "1h 50m", "2d".
// Used inside the badge where space is at a premium; the long form
// ("Reported 12 minutes ago") goes through i18n in the component.
export function formatAgeCompact(ageMs: number): string {
  if (ageMs < 60_000) return "now";
  const totalMin = Math.floor(ageMs / 60_000);
  if (totalMin < 60) return `${totalMin}m`;
  const totalHr = Math.floor(totalMin / 60);
  if (totalHr < 24) {
    const rem = totalMin - totalHr * 60;
    return rem === 0 ? `${totalHr}h` : `${totalHr}h ${rem}m`;
  }
  const days = Math.floor(totalHr / 24);
  return `${days}d`;
}
